// frontend/src/components/Modals/InvoiceUploadModal.jsx
// ✅ VERSIÓN FINAL - CON VENDEDOR CORRECTO Y COSTOS COMPLETOS
import api from '../../services/api';
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Alert,
  Card,
  CardBody,
  Table,
  Badge,
  UncontrolledTooltip
} from 'reactstrap';
import { invoicesAPI, ordersAPI } from '../../services/api';
import { buildCostBreakdown, conceptHasAmount, hasAnyCost } from '../../utils/costBreakdown';
import { parseDate } from '../../utils/dates';
import { getErrorMessage, errorToText } from '../../utils/errors';

const InvoiceUploadModal = ({ isOpen, toggle, scheduleData, onConfirm }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [orderMatch, setOrderMatch] = useState(null);
  const [tempFiles, setTempFiles] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [copied, setCopied] = useState(false);
  // 🔍 DEBUG: Ver datos completos
  useEffect(() => {
    if (scheduleData && isOpen) {
      console.group('🔍 DEBUG scheduleData');
      console.log('📦 COMPLETO:', scheduleData);
      console.log('📋 order_details:', scheduleData.order_details);
      console.log('📄 quote_details:', scheduleData.quote_details);
      console.log('🗂️ items:', scheduleData.quote_details?.items);
      console.log('💰 costs:', scheduleData.quote_details?.items?.costs);
      console.groupEnd();
    }
  }, [scheduleData, isOpen]);

  const [orderPdfUrl, setOrderPdfUrl] = useState(null);

// Mantener el PDF de la OC actualizado aunque la bandeja se haya cargado antes.
  useEffect(() => {
    let cancelled = false;

    const buildPdfUrl = (pdfFile) => {
      if (!pdfFile) return null;
      if (/^https?:\/\//i.test(pdfFile)) return pdfFile;

      const baseURL = process.env.REACT_APP_API_URL
        || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:8000');
      let pdfPath = pdfFile.replace(/\\/g, '/');
      const mediaIdx = pdfPath.indexOf('/media/');
      if (mediaIdx !== -1) {
        pdfPath = pdfPath.substring(mediaIdx + '/media/'.length);
      }
      pdfPath = pdfPath.replace(/^\/?(media\/)?/, '').replace(/^po_archive\//, '');
      return `${baseURL.replace(/\/$/, '')}/api/orders/serve-archive-pdf/?file=${encodeURIComponent(pdfPath)}`;
    };

    const refreshOrderPdf = async () => {
      const currentUrl = scheduleData?.order_pdf_url
        || buildPdfUrl(scheduleData?.order_details?.pdf_file);
      if (currentUrl) {
        setOrderPdfUrl(currentUrl);
        return;
      }

      setOrderPdfUrl(null);
      if (!isOpen || !scheduleData?.order) return;

      try {
        const response = await ordersAPI.getById(scheduleData.order);
        if (!cancelled) {
          setOrderPdfUrl(buildPdfUrl(response.data?.pdf_file));
        }
      } catch (requestError) {
        if (!cancelled) {
          console.error('No se pudo actualizar el PDF de la orden:', requestError);
          setOrderPdfUrl(null);
        }
      }
    };

    refreshOrderPdf();
    return () => {
      cancelled = true;
    };
  }, [scheduleData, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setPdfFile(null);
      setPdfPreviewUrl(null);
      setExtractedData(null);
      setOrderMatch(null);
      setTempFiles(null);
      setError(null);
      setCopied(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl && pdfPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file.type !== 'application/pdf') {
      setError('Por favor selecciona un archivo PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('El archivo es demasiado grande (máximo 10MB)');
      return;
    }

    setPdfFile(file);
    const url = URL.createObjectURL(file);
    setPdfPreviewUrl(url);
    setError(null);
  };

  // ✅ COPIAR INFORMACIÓN (CON VENDEDOR CORRECTO Y COSTOS COMPLETOS)
  const handleCopyInfo = () => {
    if (!scheduleData) return;

    const quoteItems = scheduleData.quote_details?.items || {};
    const logistics = quoteItems.logistics || {};
    // Desglose estructurado (cobrado/gastado) desde los datos crudos de la cotización.
    const costs = buildCostBreakdown(quoteItems);

    // ✅ VENDEDOR: Prioridad order.seller_name > quote.seller_name
    const elaboro = scheduleData.order_details?.seller_name || 
                   scheduleData.quote_details?.seller_name || 
                   'N/A';

    const info = `
INFORMACIÓN PARA FACTURACIÓN
═══════════════════════════════════════════════

📋 ORDEN DE COMPRA (ODC): ${scheduleData.order_number}
📅 FECHA LÍMITE: ${parseDate(scheduleData.scheduled_invoice_date).toLocaleDateString('es-MX')}
👤 ELABORÓ: ${elaboro}

🏢 DATOS DEL CLIENTE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Nombre de la Empresa: ${scheduleData.billing_name}
RFC: ${scheduleData.billing_rfc}
Dirección Fiscal: ${scheduleData.billing_address}

💰 MONTO OC Y MONTOS A FACTURAR
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Monto OC (Sin IVA): ${formatCurrency(scheduleData.order_subtotal || scheduleData.subtotal)}

Subtotal: ${formatCurrency(scheduleData.order_subtotal || scheduleData.subtotal)}
IVA (16%): ${formatCurrency(scheduleData.order_tax || scheduleData.tax)}
Total: ${formatCurrency(scheduleData.order_total || scheduleData.total)}
Días de Crédito: ${scheduleData.credit_days || 0} días

💵 DESCRIPCIÓN DE LA FACTURA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
VIÁTICOS: Cobrado ${formatCurrency(costs.viaticos?.cobrado || 0)} | Gastado ${formatCurrency(costs.viaticos?.gastado || 0)}
LUGAR: Cobrado ${formatCurrency(costs.lugar?.cobrado || 0)} | Gastado ${formatCurrency(costs.lugar?.gastado || 0)}
MATERIAL: Cobrado ${formatCurrency(costs.material?.cobrado || 0)} | Gastado ${formatCurrency(costs.material?.gastado || 0)}
OTRO: Cobrado ${formatCurrency(costs.otro?.cobrado || 0)} | Gastado ${formatCurrency(costs.otro?.gastado || 0)}

🚚 LOGÍSTICA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Viáticos: ${logistics.viaticos ? 'Sí' : 'No'}
Viáticos Cobrados: ${logistics.viaticos_charged ? 'Sí' : 'No'}
Lugar: ${logistics.lugar ? 'Sí' : 'No'}
Lugar Cobrado: ${logistics.lugar_charged ? 'Sí' : 'No'}
Costo del Lugar: ${formatCurrency(logistics.lugar_cost || 0)}

💸 COSTOS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Costo de Material: ${formatCurrency(costs.material?.cobrado || 0)}
Otros Cobrado: ${formatCurrency(costs.otro?.cobrado || 0)}
Otros Costos: ${formatCurrency(costs.otro?.gastado || 0)}

📦 DESCRIPCIÓN DEL SERVICIO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${scheduleData.description || 'N/A'}
    `.trim();

    navigator.clipboard.writeText(info).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const handleProcessPDF = async () => {
    if (!pdfFile) {
      setError('Debes seleccionar un archivo PDF primero');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // ✅ CREAR formData AQUÍ
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);

      const response = await api.post('/finance/invoices/upload-pdf/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // ✅ LOGS
      console.log('📊 DATOS EXTRAÍDOS COMPLETOS:', response.data);
      console.log('📊 TOTALES:', response.data.data?.totales);
      console.log('   • Subtotal:', response.data.data?.totales?.subtotal);
      console.log('   • Descuento:', response.data.data?.totales?.descuento);
      console.log('   • IVA Tasa:', response.data.data?.totales?.iva_tasa);
      console.log('   • IVA Importe:', response.data.data?.totales?.iva_importe);
      console.log('   • Total:', response.data.data?.totales?.total);

      if (response.data.success) {
        setExtractedData(response.data.data);
        setOrderMatch(response.data.order_match);
        setTempFiles(response.data.temp_files);
        setStep(2);
      } else {
        setError(errorToText(response.data.error) || 'Error al procesar PDF');
      }
    } catch (err) {
      console.error('Error procesando PDF:', err);
      setError(getErrorMessage(err, 'Error al procesar el PDF'));
    } finally {
      setLoading(false);
    }
  };


  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!scheduleData?.id) {
        setError('Debes seleccionar una parcialidad pendiente para confirmar la factura');
        setLoading(false);
        return;
      }

      const scheduledAmount = parseFloat(scheduleData?.scheduled_amount ?? 0) || 0;
      const extractedSubtotal = parseFloat(extractedData?.subtotal ?? 0) || 0;
      const amountDifference = Math.abs(extractedSubtotal - scheduledAmount);

      if (scheduledAmount <= 0) {
        setError('La parcialidad seleccionada no tiene un monto programado valido');
        setLoading(false);
        return;
      }

      if (amountDifference > 1) {
        setError(`El subtotal de la factura debe coincidir con la parcialidad (${formatCurrency(scheduledAmount)})`);
        setLoading(false);
        return;
      }

      const confirmData = {
        folio: extractedData.folio,
        uuid: extractedData.uuid || null,
        serie: extractedData.serie || null,
        invoice_schedule_id: scheduleData.id,
        order_id: scheduleData?.order,
        issue_date: extractedData.issue_date,
        dias_credito: scheduleData?.credit_days || 0,
        subtotal: extractedData.subtotal,
        descuento: extractedData.descuento || 0,
        iva_tasa: extractedData.iva_tasa || 0.16,
        cliente_nombre: extractedData.cliente_nombre,
        cliente_rfc: extractedData.cliente_rfc,
        cliente_domicilio: extractedData.cliente_domicilio || null,
        emisor_nombre: extractedData.emisor_nombre || 'MASARV CONSULTORIA',
        emisor_rfc: extractedData.emisor_rfc || 'MCO1610199C4',
        emisor_domicilio: extractedData.emisor_domicilio || null,
        metodo_pago: extractedData.metodo_pago || 'PPD',
        forma_pago: extractedData.forma_pago || '99',
        moneda: extractedData.moneda || 'MXN',
        conceptos: extractedData.conceptos || [],
        oc_numero: extractedData.oc_numero || null,
        observaciones: extractedData.observaciones || null,
        pdf_file_path: tempFiles?.pdf_path || '',
        xml_file_path: tempFiles?.xml_path || '',
      };

      await onConfirm(confirmData);
      toggle();
    } catch (err) {
      console.error('Error confirmando factura:', err);
      setError(getErrorMessage(err, 'Error al confirmar la factura'));
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return parseDate(dateString).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const expectedScheduleAmount = parseFloat(scheduleData?.scheduled_amount ?? 0) || 0;
  const extractedSubtotalAmount = parseFloat(extractedData?.subtotal ?? 0) || 0;
  const amountDifference = Math.abs(extractedSubtotalAmount - expectedScheduleAmount);
  const hasValidScheduleAmount = expectedScheduleAmount > 0;
  const isAmountMatch = hasValidScheduleAmount && amountDifference <= 1;
  const canConfirmInvoice = Boolean(scheduleData?.id) && isAmountMatch;
  const normalizedExtractedRfc = (extractedData?.cliente_rfc || '').trim().toUpperCase();
  const normalizedExpectedRfc = (scheduleData?.billing_rfc || '').trim().toUpperCase();

  const getServiceDetails = () => {
    if (!scheduleData) return {};
    const quoteItems = scheduleData.quote_details?.items || {};
    return quoteItems.service_details || {};
  };

  const getClientInfo = () => {
    if (!scheduleData) return {};
    const quoteItems = scheduleData.quote_details?.items || {};
    return quoteItems.client_info || {};
  };

  const getCosts = () => {
      if (!scheduleData) return {};

      // Desglose estructurado { viaticos, lugar, material, otro } con { cobrado, gastado }
      const quoteItems = scheduleData.quote_details?.items || {};
      const costs = buildCostBreakdown(quoteItems);

      console.log('💰 Costs encontrados:', costs);
      return costs;
  };


  const getLogistics = () => {
    if (!scheduleData) return {};
    const quoteItems = scheduleData.quote_details?.items || {};
    return quoteItems.logistics || {};
  };

  // ✅ OBTENER VENDEDOR CORRECTO
  const getElaboradoPor = () => {
    if (!scheduleData) return 'N/A';
    // Prioridad: order.seller_name > quote.seller_name
    return scheduleData.order_details?.seller_name || 
           scheduleData.quote_details?.seller_name || 
           'N/A';
  };

  return (
    <Modal 
      isOpen={isOpen} 
      size="xl"
      backdrop="static" 
      keyboard={false}
      className="modal-dialog-centered"
      style={{ maxWidth: '95vw' }}
    >
      <ModalHeader 
        toggle={toggle}
        className="bg-gradient-primary border-0"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h5 className="mb-0 text-white font-weight-bold">
            <i className="ni ni-single-copy-04 me-2"></i>
            {step === 1 ? 'Información para Facturar' : 'Confirmar Factura Subida'}
          </h5>
          <small className="text-white opacity-8 d-block mt-1">
            {step === 1 
              ? `Orden ${scheduleData?.order_number || ''} - Revisa la información y sube la factura generada`
              : 'Verifica que los datos extraídos sean correctos'
            }
          </small>
        </div>
      </ModalHeader>

      <ModalBody className="p-0" style={{ height: '75vh', overflow: 'hidden' }}>
        <Row className="g-0 h-100">
          <Col md="7" className="border-end" style={{ 
            overflowY: 'auto', 
            height: '100%',
            background: '#f8fafc'
          }}>
            <div className="p-4">
              {error && (
                <Alert color="" className="shadow-sm">
                  <span className="alert-icon"><i className="ni ni-support-16"></i></span>
                  <span className="alert-text">{error}</span>
                </Alert>
              )}

              {step === 1 && scheduleData && (
                <>
                  <Alert color="" className="shadow-sm mb-4">
                    <div className="">
                      <div>
                        <span className="alert-icon"><i className=""></i></span>
                        <span className="alert-text">
                          <strong>Usa esta información para generar la factura</strong>
                          <br />
                          <small>
                            1. Revisa todos los datos<br />
                            2. Ve a tu sistema de facturación (SAT/Proveedor)<br />
                            3. Genera la factura con estos datos<br />
                            4. Descarga el PDF<br />
                            5. Súbelo aquí abajo
                          </small>
                        </span>
                      </div>
                      
                    </div>
                  </Alert>

                  {/* Header con Orden, Fecha Límite y Elaboró */}
                  <Card className="shadow border-0 mb-3" style={{ borderLeft: '4px solid #5e72e4' }}>
                    <CardBody className="p-3">
                      <Row>
                        <Col md="4">
                          <small className="text-muted text-uppercase d-block mb-1">Orden de Compra (ODC)</small>
                          <h4 className="mb-0 text-primary font-weight-bold">
                            {scheduleData.order_number}
                          </h4>
                        </Col>
                        <Col md="4">
                          <small className="text-muted text-uppercase d-block mb-1">Fecha de Facturacion</small>
                          <h5 className="mb-0 text- font-weight-bold">
                            {formatDate(scheduleData.scheduled_invoice_date)}
                          </h5>
                          {scheduleData.days_until_invoice !== undefined && (
                            <Badge color={scheduleData.days_until_invoice < 0 ? 'danger' : scheduleData.days_until_invoice <= 3 ? 'warning' : 'success'} className="mt-1">
                              {scheduleData.days_until_invoice < 0 
                                ? `${Math.abs(scheduleData.days_until_invoice)} días vencido`
                                : scheduleData.days_until_invoice === 0
                                ? 'HOY'
                                : `${scheduleData.days_until_invoice} días restantes`
                              }
                            </Badge>
                          )}
                        </Col>
                        <Col md="4">
                          <small className="text-muted text-uppercase d-block mb-1">
                            <i className="ni ni-badge me-1"></i>
                            Elaboró
                          </small>
                          <h6 className="mb-0 text-dark font-weight-bold">
                            {getElaboradoPor()}
                          </h6>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* 🏢 DATOS DEL CLIENTE */}
                  <Card className="shadow border-0 mb-3">
                    <CardBody className="p-3">
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3 d-flex align-items-center">
                        <div className="icon icon-shape icon-sm bg-gradient-primary shadow text-center rounded-circle me-2">
                          <i className="ni ni-building text-white text-xs"></i>
                        </div>
                        Datos del Cliente (Receptor)
                      </h6>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted font-weight-bold" style={{ width: '35%' }}>Nombre de la Empresa:</td>
                            <td className="font-weight-bold text-dark">{scheduleData.billing_name}</td>
                          </tr>
                          <tr>
                            <td className="text-muted font-weight-bold">RFC:</td>
                            <td className="font-weight-bold text-dark">
                              <code className="text-primary">{scheduleData.billing_rfc}</code>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted font-weight-bold">Dirección Fiscal:</td>
                            <td className="font-weight-bold text-dark" style={{ fontSize: '0.875rem' }}>
                              {scheduleData.billing_address}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>

                  {/* 💰 MONTOS */}
                  <Card className="shadow border-0 mb-3">
                    <CardBody className="p-3">
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3 d-flex align-items-center">
                        <div className="icon icon-shape icon-sm bg-gradient-success shadow text-center rounded-circle me-2">
                          <i className="ni ni-money-coins text-white text-xs"></i>
                        </div>
                        Monto OC y Montos a Facturar
                      </h6>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            
                          </tr>
                          <tr className="border-top">
                            <td className="text-muted">Subtotal:</td>
                            <td className="text-end font-weight-bold" style={{ fontSize: '1rem' }}>
                              {formatCurrency(scheduleData.order_subtotal || scheduleData.subtotal)}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted">IVA (16%):</td>
                            <td className="text-end font-weight-bold" style={{ fontSize: '1rem' }}>
                              {formatCurrency(scheduleData.order_tax || scheduleData.tax)}
                            </td>
                          </tr>
                          <tr className="border-top">
                            <td className="text-muted font-weight-bold">Total:</td>
                            <td className="text-end font-weight-bold text-primary" style={{ fontSize: '1.25rem' }}>
                              {formatCurrency(scheduleData.order_total || scheduleData.total)}
                            </td>
                          </tr>
                          <tr className="border-top">
                            <td className="text-muted font-weight-bold">Monto de esta parcialidad:</td>
                            <td className="text-end font-weight-bold text-success" style={{ fontSize: '1.1rem' }}>
                              {formatCurrency(scheduleData.scheduled_amount || 0)}
                            </td>
                          </tr>
                          <tr className="border-top">
                            <td className="text-muted">Días de Crédito:</td>
                            <td className="text-end font-weight-bold">
                              {scheduleData.credit_days || 0} días
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
{/* 💵 DESCRIPCIÓN DE LA FACTURA (COSTOS) */}
                            {(() => {
                              const costs = getCosts();

                              // Si no hay ningún costo, no mostrar nada
                              if (!hasAnyCost(costs)) return null;

                              const costRows = [
                                ['VIÁTICOS', costs.viaticos],
                                ['LUGAR', costs.lugar],
                                ['MATERIAL', costs.material],
                                ['OTRO', costs.otro],
                              ];

                              return (
                                <Col md="12" className="mb-3">
                                  <Card className="shadow-sm border-0">
                                    <CardBody className="p-3">
                                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                                        <i className="ni ni-chart-bar-32 me-2"></i>
                                        Descripción de la Factura (Desglose de Costos)
                                      </h6>
                                      <Table size="sm" className="mb-0" bordered hover>
                                        <thead className="thead-light">
                                          <tr>
                                            <th className="text-xs font-weight-bold">Concepto</th>
                                            <th className="text-xs text-end font-weight-bold">Cobrado</th>
                                            <th className="text-xs text-end font-weight-bold">Gastado</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {costRows.map(([label, c]) =>
                                            conceptHasAmount(c) ? (
                                              <tr key={label}>
                                                <td className="text-sm font-weight-bold">{label}</td>
                                                <td className="text-sm text-end font-weight-bold text-success">
                                                  {formatCurrency(c.cobrado || 0)}
                                                </td>
                                                <td className="text-sm text-end font-weight-bold text-danger">
                                                  {formatCurrency(c.gastado || 0)}
                                                </td>
                                              </tr>
                                            ) : null
                                          )}
                                        </tbody>
                                      </Table>
                                    </CardBody>
                                  </Card>
                                </Col>
                              );
                            })()}
                            {/* 📦 DESCRIPCIÓN DEL SERVICIO */}
                  <Card className="shadow border-0 mb-3">
                    <CardBody className="p-3">
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3 d-flex align-items-center">
                        <div className="icon icon-shape icon-sm bg-gradient-info shadow text-center rounded-circle me-2">
                          <i className="ni ni-hat-3 text-white text-xs"></i>
                        </div>
                        Detalles del Servicio
                      </h6>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          {(() => {
                            const serviceDetails = getServiceDetails();
                            const items = scheduleData.quote_details?.items || {};
                            
                            return (
                              <>
                                {/* Categoría */}
                                {(serviceDetails.category || items.category) && (
                                  <tr>
                                    <td className="text-muted font-weight-bold" style={{ width: '35%' }}>Categoría:</td>
                                    <td className="font-weight-bold text-dark">
                                      <Badge color="primary">{serviceDetails.category || items.category}</Badge>
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Tipo de Servicio */}
                                {(serviceDetails.service_type || items.service_type) && (
                                  <tr>
                                    <td className="text-muted font-weight-bold">Tipo de Servicio:</td>
                                    <td className="font-weight-bold text-dark">
                                      {serviceDetails.service_type || items.service_type}
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Horas Totales */}
                                {scheduleData.total_hours && (
                                  <tr>
                                    <td className="text-muted font-weight-bold">Horas Totales:</td>
                                    <td className="font-weight-bold text-dark">
                                      <Badge color="info">{scheduleData.total_hours} hrs</Badge>
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Sesiones */}
                                {(serviceDetails.sessions || items.sessions) && (
                                  <tr>
                                    <td className="text-muted font-weight-bold">Sesiones:</td>
                                    <td className="font-weight-bold text-dark">
                                      <Badge color="info">{serviceDetails.sessions || items.sessions}</Badge>
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Personas */}
                                {(serviceDetails.num_people || items.num_people) && (
                                  <tr>
                                    <td className="text-muted font-weight-bold">Personas:</td>
                                    <td className="font-weight-bold text-dark">
                                      <Badge color="primary">{serviceDetails.num_people || items.num_people}</Badge>
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Grupos */}
                                {(serviceDetails.num_groups || items.num_groups) && (
                                  <tr>
                                    <td className="text-muted font-weight-bold">Grupos:</td>
                                    <td className="font-weight-bold text-dark">
                                      <Badge color="primary">{serviceDetails.num_groups || items.num_groups}</Badge>
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Idioma */}
                                {(serviceDetails.language || items.language) && (
                                  <tr>
                                    <td className="text-muted font-weight-bold">Idioma:</td>
                                    <td className="font-weight-bold text-dark">
                                      {serviceDetails.language || items.language}
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Ciudad */}
                                {(serviceDetails.city || items.city || scheduleData.location) && (
                                  <tr>
                                    <td className="text-muted font-weight-bold">Ciudad:</td>
                                    <td className="font-weight-bold text-dark">
                                      {serviceDetails.city || items.city || scheduleData.location}
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Incluye */}
                                {(serviceDetails.includes || items.includes) && (
                                  <tr>
                                    <td className="text-muted font-weight-bold">Incluye:</td>
                                    <td className="font-weight-bold text-dark">
                                      {serviceDetails.includes || items.includes}
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Costo por Hora */}
                                {scheduleData.hourly_rate && (
                                  <tr>
                                    <td className="text-muted font-weight-bold">Costo por Hora:</td>
                                    <td className="font-weight-bold text-success">
                                      {formatCurrency(scheduleData.hourly_rate)}
                                    </td>
                                  </tr>
                                )}
                                
                                {/* Descripción (si existe) */}
                                {scheduleData.description && (
                                  <tr>
                                    <td className="text-muted font-weight-bold">Descripción:</td>
                                    <td className="text-dark" style={{ whiteSpace: 'pre-line' }}>
                                      {scheduleData.description}
                                    </td>
                                  </tr>
                                )}
                              </>
                            );
                          })()}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>

                  {/* 🚚 LOGÍSTICA Y ENTREGA */}
                  <Card className="shadow border-0 mb-3">
                    <CardBody className="p-3">
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3 d-flex align-items-center">
                        <div className="icon icon-shape icon-sm bg-gradient-warning shadow text-center rounded-circle me-2">
                          <i className="ni ni-delivery-fast text-white text-xs"></i>
                        </div>
                        Logística y Entrega
                      </h6>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          {/* Fecha de Impartición */}
                          {scheduleData.service_delivery_date && (
                            <tr>
                              <td className="text-muted font-weight-bold" style={{ width: '35%' }}>Fecha de Impartición:</td>
                              <td className="font-weight-bold text-dark">
                                {formatDate(scheduleData.service_delivery_date)}
                              </td>
                            </tr>
                          )}
                          
                          {/* Fecha de Orden */}
                          {scheduleData.order_details?.order_date && (
                            <tr>
                              <td className="text-muted font-weight-bold">Fecha de Orden:</td>
                              <td className="font-weight-bold text-dark">
                                {formatDate(scheduleData.order_details.order_date)}
                              </td>
                            </tr>
                          )}
                          
                          {/* Fecha Programada Factura */}
                          {scheduleData.scheduled_invoice_date && (
                            <tr>
                              <td className="text-muted font-weight-bold">Fecha Programada Factura:</td>
                              <td className="font-weight-bold text-dark">
                                {formatDate(scheduleData.scheduled_invoice_date)}
                              </td>
                            </tr>
                          )}
                          
                          {/* Facilitador */}
                          {scheduleData.facilitator_name && (
                            <tr>
                              <td className="text-muted font-weight-bold">Facilitador:</td>
                              <td className="font-weight-bold text-dark">
                                {scheduleData.facilitator_name}
                              </td>
                            </tr>
                          )}
                          
                          {/* Lugar */}
                          <tr>
                            <td className="text-muted font-weight-bold">Lugar:</td>
                            <td className="font-weight-bold text-dark">
                              {scheduleData.location || 'N/A'}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                            
                  {/* 🚚 LOGÍSTICA */}
                  {(() => {
                    const logistics = getLogistics();
                    
                    return (
                      <Card className="shadow border-0 mb-3">
                        <CardBody className="p-3">
                          <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3 d-flex align-items-center">
                            <div className="icon icon-shape icon-sm bg-gradient-info shadow text-center rounded-circle me-2">
                              <i className="ni ni-delivery-fast text-white text-xs"></i>
                            </div>
                            Logística
                          </h6>
                          <Table borderless size="sm" className="mb-0">
                            <tbody>
                              <tr>
                                <td className="text-muted font-weight-bold" style={{ width: '40%' }}>Viáticos:</td>
                                <td className="font-weight-bold text-dark">
                                  {logistics.viaticos ? 'Sí' : 'No'}
                                </td>
                              </tr>
                              <tr>
                                <td className="text-muted font-weight-bold">Viáticos Cobrados:</td>
                                <td className="font-weight-bold text-dark">
                                  {logistics.viaticos_charged ? 'Sí' : 'No'}
                                </td>
                              </tr>
                              <tr>
                                <td className="text-muted font-weight-bold">Lugar:</td>
                                <td className="font-weight-bold text-dark">
                                  {logistics.lugar ? 'Sí' : 'No'}
                                </td>
                              </tr>
                              <tr>
                                <td className="text-muted font-weight-bold">Lugar Cobrado:</td>
                                <td className="font-weight-bold text-dark">
                                  {logistics.lugar_charged ? 'Sí' : 'No'}
                                </td>
                              </tr>
                              <tr>
                                <td className="text-muted font-weight-bold">Costo del Lugar:</td>
                                <td className="font-weight-bold text-dark">
                                  {formatCurrency(logistics.lugar_cost || 0)}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </CardBody>
                      </Card>
                    );
                  })()}

                  

                  {/* ... resto del código (Descripción del Servicio, Contacto, Notas, Zona de Subida) ... */}
                  {/* (Mantener igual que antes) */}
                  {/* 📝 NOTAS Y DETALLES DEL TALLER */}
                  {(scheduleData.notes || scheduleData.workshop_name || scheduleData.entry_requirements || scheduleData.session_schedule || scheduleData.program_required || scheduleData.diplomas_required) && (
                    <Card className="shadow border-0 mb-3">
                      <CardBody className="p-3">
                        <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3 d-flex align-items-center">
                          <div className="icon icon-shape icon-sm bg-gradient-secondary shadow text-center rounded-circle me-2">
                            <i className="ni ni-align-left-2 text-white text-xs"></i>
                          </div>
                          Notas y Detalles del Taller
                        </h6>
                        <Table borderless size="sm" className="mb-0">
                          <tbody>
                            {scheduleData.workshop_name && (
                              <tr>
                                <td className="text-muted font-weight-bold" style={{ width: '35%' }}>Nombre del Taller:</td>
                                <td className="font-weight-bold text-dark">{scheduleData.workshop_name}</td>
                              </tr>
                            )}
                            {scheduleData.session_schedule && (
                              <tr>
                                <td className="text-muted font-weight-bold">Horario:</td>
                                <td className="font-weight-bold text-dark">{scheduleData.session_schedule}</td>
                              </tr>
                            )}
                            {scheduleData.entry_requirements && (
                              <tr>
                                <td className="text-muted font-weight-bold">Requisitos de Ingreso:</td>
                                <td className="text-dark" style={{ whiteSpace: 'pre-line' }}>{scheduleData.entry_requirements}</td>
                              </tr>
                            )}
                            <tr>
                              <td className="text-muted font-weight-bold">¿Es Programa?</td>
                              <td>
                                <Badge color={scheduleData.program_required ? 'success' : 'secondary'} className="badge-sm">
                                  {scheduleData.program_required ? 'Sí' : 'No'}
                                </Badge>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-muted font-weight-bold">¿Requiere Diplomas?</td>
                              <td>
                                <Badge color={scheduleData.diplomas_required ? 'success' : 'secondary'} className="badge-sm">
                                  {scheduleData.diplomas_required ? 'Sí' : 'No'}
                                </Badge>
                              </td>
                            </tr>
                            {scheduleData.notes && (
                              <tr>
                                <td className="text-muted font-weight-bold">Notas:</td>
                                <td className="text-dark" style={{ whiteSpace: 'pre-line' }}>{scheduleData.notes}</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  )}

                  {/* ✅ ZONA DE SUBIDA */}
                  <Card className="shadow border-0" style={{ borderLeft: '4px solid #2dce89' }}>
                    <CardBody>
                                            <h6 className="text-uppercase text-xs font-weight-bold text-success mb-3 d-flex align-items-center">
                        <div className="icon icon-shape icon-sm bg-gradient-success shadow text-center rounded-circle me-2">
                          <i className="ni ni-cloud-upload-96 text-white text-xs"></i>
                        </div>
                        Subir Factura Generada (PDF)
                      </h6>
                      
                      <Alert color="" className="mb-3">
                        <small>
                          <i className="ni ni-bell-55 me-1"></i>
                          <strong>Importante:</strong> Asegúrate de haber generado la factura en tu sistema del SAT con los datos mostrados arriba.
                        </small>
                      </Alert>

                      <div
                        className={`border-2 border-dashed rounded-lg p-4 text-center ${
                          dragActive ? 'border-success bg-success-light' : 'border-gray-300'
                        }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        style={{
                          borderStyle: 'dashed',
                          borderWidth: '2px',
                          borderColor: dragActive ? '#2dce89' : '#dee2e6',
                          backgroundColor: dragActive ? '#f0fdf4' : '#ffffff',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div className="mb-3">
                          <i 
                            className={`ni ni-cloud-upload-96 ${dragActive ? 'text-success' : 'text-muted'}`}
                            style={{ fontSize: '2.5rem' }}
                          ></i>
                        </div>
                        
                        {pdfFile ? (
                          <div>
                            <Badge color="primary" className="mb-2">
                              <i className="ni ni-check-bold me-1"></i>
                              Factura cargada
                            </Badge>
                            <p className="mb-1 font-weight-bold text-dark">{pdfFile.name}</p>
                            <p className="text-sm text-muted mb-3">
                              {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                            <Button
                              color="secondary"
                              size="sm"
                              outline
                              onClick={() => {
                                setPdfFile(null);
                                setPdfPreviewUrl(null);
                              }}
                            >
                              <i className="ni ni-fat-remove me-2"></i>
                              Cambiar archivo
                            </Button>
                          </div>
                        ) : (
                          <>
                            <h6 className="text-muted mb-2">
                              Arrastra y suelta tu factura PDF aquí
                            </h6>
                            <p className="text-xs text-muted mb-3">
                              o haz clic para seleccionar
                            </p>
                            <input
                              type="file"
                              id="file-upload"
                              accept="application/pdf"
                              onChange={handleFileInput}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="file-upload">
                              <Button color="primary" size="sm" tag="span">
                                <i className="ni ni-folder-17 me-2"></i>
                                Seleccionar PDF de Factura
                              </Button>
                            </label>
                          </>
                        )}
                      </div>

                      {pdfFile && (
                        <div className="text-center mt-3">
                          <Button
                            color="primary"
                            size="lg"
                            onClick={handleProcessPDF}
                            disabled={loading}
                            block
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Procesando factura...
                              </>
                            ) : (
                              <>
                                <i className="ni ni-settings-gear-65 me-2"></i>
                                Procesar Factura y Extraer Datos
                              </>
                            )}
                          </Button>
                          <small className="text-muted d-block mt-2">
                            El sistema extraerá automáticamente los datos del PDF
                          </small>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </>
              )}

              {/* ========================================== */}
              {/* STEP 2: CONFIRMACIÓN DE DATOS EXTRAÍDOS */}
              {/* ========================================== */}
              {step === 2 && extractedData && (
                <>
                  {orderMatch?.exact_match && (
                    <Alert color="success" className="shadow-sm mb-4">
                      <span className="alert-icon"><i className="ni ni-like-2"></i></span>
                      <span className="alert-text">
                        <strong>¡Orden encontrada automáticamente!</strong>
                        <br />
                        Orden #{orderMatch.exact_match.order_number} - {orderMatch.exact_match.client_name}
                        <br />
                        <small>Tipo de match: <code>{orderMatch.match_type}</code> | Confianza: <Badge color="success">{orderMatch.confidence}%</Badge></small>
                      </span>
                    </Alert>
                  )}

                  {!orderMatch?.exact_match && (
                    <Alert color="warning" className="shadow-sm mb-4">
                      <span className="alert-icon"><i className="ni ni-bell-55"></i></span>
                      <span className="alert-text">
                        <strong>No se encontró match automático</strong>
                        <br />
                        <small>Se usará la orden seleccionada: {scheduleData?.order_number}</small>
                      </span>
                    </Alert>
                  )}

                  <Card className="shadow border-0 mb-3">
                    <CardBody>
                      <h6 className="text-primary mb-3 d-flex align-items-center">
                        <div className="icon icon-shape icon-sm bg-gradient-primary shadow text-center rounded-circle me-2">
                          <i className="ni ni-single-copy-04 text-white text-xs"></i>
                        </div>
                        Datos Extraídos de la Factura
                      </h6>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted font-weight-bold" style={{ width: '35%' }}>Folio:</td>
                            <td className="font-weight-bold text-dark">
                              <code className="text-primary">{extractedData.folio || 'N/A'}</code>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted font-weight-bold">Fecha Emisión:</td>
                            <td className="font-weight-bold text-dark">{extractedData.issue_date || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td className="text-muted font-weight-bold">UUID (Folio Fiscal):</td>
                            <td className="font-weight-bold text-dark" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                              {extractedData.uuid || 'N/A'}
                            </td>
                          </tr>
                          {extractedData.serie && (
                            <tr>
                              <td className="text-muted font-weight-bold">Serie:</td>
                              <td className="font-weight-bold text-dark">{extractedData.serie}</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>

                  <Card className="shadow border-0 mb-3">
                    <CardBody>
                      <h6 className="text-primary mb-3 d-flex align-items-center">
                        <div className="icon icon-shape icon-sm bg-gradient-info shadow text-center rounded-circle me-2">
                          <i className="ni ni-building text-white text-xs"></i>
                        </div>
                        Datos del Receptor (Cliente)
                      </h6>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted font-weight-bold" style={{ width: '35%' }}>Razón Social:</td>
                            <td className="font-weight-bold text-dark">{extractedData.cliente_nombre || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td className="text-muted font-weight-bold">RFC:</td>
                            <td className="font-weight-bold text-dark">
                              <code className="text-primary">{extractedData.cliente_rfc || 'N/A'}</code>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted font-weight-bold">Método de Pago:</td>
                            <td className="font-weight-bold text-dark">
                              {extractedData.metodo_pago || 'PPD'}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted font-weight-bold">Forma de Pago:</td>
                            <td className="font-weight-bold text-dark">
                              {extractedData.forma_pago || '99'}
                            </td>
                          </tr>
                        </tbody>
                      </Table>

                      {scheduleData && normalizedExtractedRfc !== normalizedExpectedRfc && (
                        <Alert color="warning" className="mt-3 mb-0">
                          <small>
                            <i className="ni ni-bell-55 me-1"></i>
                            <strong>Advertencia:</strong> El RFC difiere del esperado ({scheduleData.billing_rfc})
                          </small>
                        </Alert>
                      )}
                    </CardBody>
                  </Card>

                  <Card className="shadow border-0 mb-3">
                    <CardBody>
                      <h6 className="text-primary mb-3 d-flex align-items-center">
                        <div className="icon icon-shape icon-sm bg-gradient-success shadow text-center rounded-circle me-2">
                          <i className="ni ni-money-coins text-white text-xs"></i>
                        </div>
                        Montos Extraídos
                      </h6>
                      <Table borderless size="sm">
                        <tbody>
                          <tr>
                            <td className="text-muted">Subtotal:</td>
                            <td className="text-end font-weight-bold">{formatCurrency(extractedData.subtotal)}</td>
                          </tr>
                          {extractedData.descuento > 0 && (
                            <tr>
                              <td className="text-muted">Descuento:</td>
                              <td className="text-end text-danger font-weight-bold">
                                -{formatCurrency(extractedData.descuento)}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td className="text-muted">IVA ({(extractedData.iva_tasa * 100).toFixed(0)}%):</td>
                            <td className="text-end font-weight-bold">{formatCurrency(extractedData.iva_importe)}</td>
                          </tr>
                          <tr className="border-top">
                            <td className="text-muted font-weight-bold">Total:</td>
                            <td className="text-end font-weight-bold text-success" style={{ fontSize: '1.25rem' }}>
                              {formatCurrency(extractedData.total)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>

                      {scheduleData && hasValidScheduleAmount && !isAmountMatch && (
                        <Alert color="warning" className="mt-3 mb-0">
                          <small>
                            <i className="ni ni-bell-55 me-1"></i>
                            <strong>Advertencia:</strong> El monto difiere de la parcialidad ({formatCurrency(expectedScheduleAmount)})
                            <br />
                            <span className="text-muted">Diferencia: {formatCurrency(amountDifference)}</span>
                          </small>
                        </Alert>
                      )}

                      {scheduleData && hasValidScheduleAmount && isAmountMatch && (
                        <Alert color="success" className="mt-3 mb-0">
                          <small>
                            <i className="ni ni-check-bold me-1"></i>
                            <strong>Validación exitosa:</strong> El monto coincide con la parcialidad
                          </small>
                        </Alert>
                      )}
                    </CardBody>
                  </Card>

                  {extractedData.conceptos && extractedData.conceptos.length > 0 && (
                    <Card className="shadow border-0 mb-3">
                      <CardBody>
                        <h6 className="text-primary mb-3 d-flex align-items-center">
                          <div className="icon icon-shape icon-sm bg-gradient-warning shadow text-center rounded-circle me-2">
                            <i className="ni ni-bullet-list-67 text-white text-xs"></i>
                          </div>
                          Conceptos Facturados ({extractedData.conceptos.length})
                        </h6>
                        <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                          <Table size="sm" className="mb-0" hover>
                            <thead className="thead-light">
                              <tr>
                                <th className="text-xs">#</th>
                                <th className="text-xs">Descripción</th>
                                <th className="text-xs text-center">Cant.</th>
                                <th className="text-xs text-end">Importe</th>
                              </tr>
                            </thead>
                            <tbody>
                              {extractedData.conceptos.map((concepto, index) => (
                                <tr key={index}>
                                  <td className="text-xs text-muted">{index + 1}</td>
                                  <td className="text-xs">{concepto.descripcion}</td>
                                  <td className="text-xs text-center">{concepto.cantidad}</td>
                                  <td className="text-xs text-end font-weight-bold">{formatCurrency(concepto.importe)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {extractedData.oc_numero && (
                    <Card className="shadow border-0 mb-3">
                      <CardBody>
                        <h6 className="text-primary mb-3 d-flex align-items-center">
                          <div className="icon icon-shape icon-sm bg-gradient-secondary shadow text-center rounded-circle me-2">
                            <i className="ni ni-tag text-white text-xs"></i>
                          </div>
                          Referencias
                        </h6>
                        <Table borderless size="sm" className="mb-0">
                          <tbody>
                            <tr>
                              <td className="text-muted font-weight-bold" style={{ width: '35%' }}>Número de OC:</td>
                              <td className="font-weight-bold text-dark">
                                <code className="text-primary">{extractedData.oc_numero}</code>
                              </td>
                            </tr>
                            {extractedData.observaciones && (
                              <tr>
                                <td className="text-muted font-weight-bold">Observaciones:</td>
                                <td className="font-weight-bold text-dark" style={{ fontSize: '0.875rem' }}>
                                  {extractedData.observaciones}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  )}
                </>
              )}
            </div>
          </Col>

          {/* ========================================== */}
          {/* COLUMNA DERECHA: PDF VIEWER (40%) */}
          {/* ========================================== */}
          <Col md="5" className="bg-white" style={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div 
              className="p-3 border-bottom"
              style={{
                background: 'linear-gradient(to right, #f8fafc, #e8f5f4)',
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0 font-weight-bold text-dark">
                    <i className="ni ni-single-copy-04 me-2 text-primary"></i>
                    Vista Previa del PDF
                  </h6>
                  <small className="text-muted">
                    {step === 1 
                      ? (pdfFile ? pdfFile.name : `Orden ${scheduleData?.order_number || ''}`)
                      : (pdfFile ? `Factura ${extractedData?.folio || ''}` : 'Sin archivo')
                    }
                  </small>
                </div>
                {(pdfPreviewUrl || orderPdfUrl) && (
                  <Button
                    color="primary"
                    size="sm"
                    className="btn-icon"
                    onClick={() => window.open(pdfPreviewUrl || orderPdfUrl, '_blank')}
                    title="Abrir en nueva pestaña"
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-laptop"></i>
                    </span>
                  </Button>
                )}
              </div>
            </div>

            <div 
              className="flex-grow-1 position-relative"
              style={{ 
                background: '#f1f5f9',
                overflow: 'hidden'
              }}
            >
              {pdfPreviewUrl ? (
                <iframe
                  src={pdfPreviewUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="Vista Previa Factura"
                />
              ) : orderPdfUrl && step === 1 ? (
                <iframe
                  src={`${orderPdfUrl}#toolbar=1&navpanes=0`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="Vista Previa Orden de Compra"
                />
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 p-4">
                  <div 
                    className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle mb-3"
                    style={{ width: '80px', height: '80px' }}
                  >
                    <i className="ni ni-cloud-upload-96 text-white" style={{ fontSize: '2rem', lineHeight: '80px' }}></i>
                  </div>
                  <h5 className="text-dark text-center">
                    {step === 1 ? 'Esperando factura PDF' : 'Sin vista previa'}
                  </h5>
                  <p className="text-sm text-muted text-center px-4">
                    {step === 1 
                      ? 'Selecciona o arrastra el PDF de la factura generada para ver la vista previa'
                      : 'El PDF se mostrará después de subirlo'
                    }
                  </p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="border-0 bg-light">
        <Button 
          color="secondary" 
          onClick={() => {
            if (step === 2) {
              setStep(1);
              setExtractedData(null);
              setOrderMatch(null);
              setTempFiles(null);
            } else {
              toggle();
            }
          }} 
          disabled={loading}
        >
          <i className={`ni ni-${step === 2 ? 'bold-left' : 'fat-remove'} me-2`}></i>
          {step === 2 ? 'Atrás' : 'Cancelar'}
        </Button>
        
        {step === 2 && (
          <Button 
            color="success" 
            onClick={handleConfirm} 
            disabled={loading || !canConfirmInvoice}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Guardando factura...
              </>
            ) : (
              <>
                <i className="ni ni-check-bold me-2"></i>
                Confirmar y Guardar Factura
              </>
            )}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default InvoiceUploadModal;

                      

